import { Component, Output, EventEmitter, inject, signal, effect, computed, HostListener } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { environment } from '@environments/environment';
import { filter } from 'rxjs/operators';

// Services
import { AuthService } from '@core/services/auth-new.service';
import { EnhancedThemeService } from '@core/theme/services/enhanced-theme.service';
import { SearchService } from '@core/services/search.service';
import { CustomerProfileService } from '@core/services/customer-profile.service';
import { NotificationService } from '@core/services/notification.service';
import { ApiClientService, ApiResponse } from '@core/services/api-client.service';
import { CompanyService } from '@core/services/company.service';
import { PermissionService } from '@shared/permission-service.ts/resorces-permission.service';

// UI Components
import { DropdownComponent } from '@shared/components/ui/dropdown';
import { CompanySwitcherComponent } from './components/company-switcher/company-switcher.component';
import { UserProfileComponent } from '@features/settings/pages/user-control/user-profile';
import { SendDataService } from '@core/services/send-data.service';

// Modules
import { SharedModule } from '@shared/shared.module';

// Interfaces
import { SearchCustomer } from '@shared/interfaces/search.interfaces';
import { ResizablePanelComponent } from '@shared/components/common-components/resizable-panel.component';

interface User {
  name: string;
  id?: string;
  avatar?: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, DropdownComponent, FormsModule, CompanySwitcherComponent, UserProfileComponent, ResizablePanelComponent, SharedModule],
  template: `
    <header class="header-container">
      <!-- Desktop Header -->
      <div class="header-content desktop-header">
        <!-- Left Section - Company Switcher -->
        <app-company-switcher (dropdownOpenChange)="companySwitcherOpenChange.emit($event)"></app-company-switcher>

        <!-- Center Section - Search Bar -->
        <div class="search-section" style="position: relative;">
          <div class="search-container" [class.dropdown-open]="showAdvancedSearch() || (showGlobalSearch() && showGlobalSearchContainer())">
            <div class="search-input-group">
              <div class="search-icon-container">
                <!-- Search Icon -->
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M17.4998 17.4993L13.9165 13.916" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

              </div>

              <!-- Search Input -->
              <input
                type="text"
                [(ngModel)]="searchQuery"
                (input)="onSearchInput($event)"
                (click)="onSearchInputClick($event)"
                (keydown)="onSearchKeydown($event)"
                placeholder="Search"
                class="search-input"
                data-search-input="global-header-search"
                autocomplete="off"
                #searchInput
              />

              <!-- Command K Badge -->
              <div class="command-badge">
                <span>{{ getKeyboardShortcut() }}</span>
              </div>
            </div>

            <!-- Filter Icon -->
            <button
              class="filter-btn"
              (click)="toggleFilter()"
              [class.active]="showFilter()"
              aria-label="Toggle filters"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M2.5 3.75C2.5 3.41848 2.6317 3.10054 2.86612 2.86612C3.10054 2.6317 3.41848 2.5 3.75 2.5H16.25C16.5815 2.5 16.8995 2.6317 17.1339 2.86612C17.3683 3.10054 17.5 3.41848 17.5 3.75V5.48833C17.4999 5.93032 17.3242 6.35418 17.0117 6.66667L12.5 11.1783V17.365C12.5 17.5213 12.4601 17.6749 12.384 17.8114C12.3079 17.9479 12.1982 18.0627 12.0653 18.1449C11.9324 18.227 11.7806 18.2739 11.6245 18.2809C11.4684 18.2879 11.3131 18.2549 11.1733 18.185L8.07583 16.6367C7.90281 16.5502 7.75729 16.4172 7.65559 16.2526C7.55388 16.0881 7.50001 15.8984 7.5 15.705V11.1783L2.98833 6.66667C2.67575 6.35418 2.50009 5.93032 2.5 5.48833V3.75ZM4.16667 4.16667V5.48833L8.8 10.1217C8.91621 10.2377 9.0084 10.3756 9.07132 10.5273C9.13423 10.679 9.16663 10.8416 9.16667 11.0058V15.3183L10.8333 16.1517V11.0058C10.8333 10.6742 10.965 10.3558 11.2 10.1225L15.8333 5.4875V4.16667H4.16667Z"
                  fill="#808080"
                />
              </svg>
            </button>
          </div>

          <!-- Global Search Dropdown (only when user has permission) -->
          @if ((showGlobalSearch() || isClosingGlobalSearch()) && !showAdvancedSearch() && showGlobalSearchContainer()) {
            <div class="global-search-dropdown" [class.closing]="isClosingGlobalSearch()" (click)="$event.stopPropagation()">
              <app-global-search
                [text]="searchQuery()"
                [tabPermissions]="globalSearchTabPermissions()"
                (closeGlobalSearch)="closeGlobalSearch()"
              ></app-global-search>
            </div>
          }

          <!-- Advanced Search Dropdown -->
          @if (showAdvancedSearch()) {
            <div class="global-search-dropdown advanced-search-mode" (click)="$event.stopPropagation()">
              <app-advanced-search
                (closeAdvancedSearch)="closeAdvancedSearch()"
              ></app-advanced-search>
            </div>
          }
        </div>

        <!-- Right Section - Actions & Profile -->
        <div class="actions-section">
          <!-- Action Icons -->
          <div class="action-icons">
            <!-- Theme Toggle -->
            <button
              (click)="themeService.toggleTheme()"
              class="theme-toggle-btn"
              aria-label="Toggle theme"
            >
              @if (isDarkMode()) {
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 15C12.7614 15 15 12.7614 15 10C15 7.23858 12.7614 5 10 5C7.23858 5 5 7.23858 5 10C5 12.7614 7.23858 15 10 15Z" stroke="white" stroke-width="1.25"/>
                  <path d="M10.0003 1.66675V2.50008M10.0003 17.5001V18.3334M18.3337 10.0001H17.5003M2.50033 10.0001H1.66699M15.892 4.10841L15.5653 4.43591M4.43533 15.5651L4.10783 15.8926M15.892 15.8917L15.5653 15.5642M4.43533 4.43508L4.10783 4.10758" stroke="white" stroke-width="1.25" stroke-linecap="round"/>
                </svg>
              } @else {
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M6.25 5.3125C6.25 4.11641 6.42617 2.90586 6.875 1.875C3.88945 3.17461 1.875 6.22344 1.875 9.6875C1.875 14.3473 5.65273 18.125 10.3125 18.125C13.7766 18.125 16.8254 16.1105 18.125 13.125C17.0941 13.5738 15.8836 13.75 14.6875 13.75C10.0277 13.75 6.25 9.97227 6.25 5.3125Z"
                    stroke="black"
                    stroke-width="1.25"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              }
            </button>
          </div>

          <!-- User Profile Dropdown -->
          <app-dropdown position="bottom-right">
            <!-- Trigger -->
            <button
              slot="trigger"
              class="profile-trigger bg-theme-bg-card border border-light"
              aria-label="User menu"
            >
              <!-- User Info -->
              <div class="profile-info">
                <!-- Avatar -->
                <div class="user-avatar"
                     [style.background-color]="getUserColorPair().bg"
                     [style.--avatar-text-color]="getUserColorPair().text">
                  <span class="user-initials">{{ getUserInitials() }}</span>
                </div>

                <!-- Name -->
                <span class="user-name">{{ currentUser().name }}</span>
              </div>

              <!-- Menu Dots -->
              <div class="menu-dots">
                <svg width="3" height="15" viewBox="0 0 3 15" fill="none">
                  <circle cx="1.5" cy="1.5" r="1.5" fill="currentColor" />
                  <circle cx="1.5" cy="7.5" r="1.5" fill="currentColor" />
                  <circle cx="1.5" cy="13.5" r="1.5" fill="currentColor" />
                </svg>
              </div>
            </button>

            <!-- Dropdown Content -->
            <div slot="content" class="profile-dropdown-content">
              <div class="dropdown-menu">
                <!-- Profile -->
                <button class="dropdown-item" (click)="openProfileModal();">
                  <div class="dropdown-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M9 9C11.21 9 12.5 7.21 12.5 5C12.5 2.79 11.21 1 9 1C6.79 1 5.5 2.79 5.5 5C5.5 7.21 6.79 9 9 9Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M15.59 17C15.59 14.24 12.69 12 9 12C5.31 12 2.41 14.24 2.41 17"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <span>Profile</span>
                </button>

                <!-- Settings -->
                <button class="dropdown-item" (click)="navigateToSettings()">
                  <div class="dropdown-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M9 11.25C10.24 11.25 11.25 10.24 11.25 9C11.25 7.76 10.24 6.75 9 6.75C7.76 6.75 6.75 7.76 6.75 9C6.75 10.24 7.76 11.25 9 11.25Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M14.73 9.79C14.76 9.53 14.78 9.27 14.78 9C14.78 8.73 14.76 8.47 14.73 8.21L16.26 7.05C16.4 6.93 16.44 6.73 16.35 6.55L14.85 3.95C14.76 3.77 14.56 3.7 14.38 3.76L12.6 4.44C12.24 4.18 11.85 3.96 11.43 3.79L11.18 1.86C11.15 1.66 10.98 1.5 10.78 1.5H7.78C7.58 1.5 7.41 1.66 7.38 1.86L7.13 3.79C6.71 3.96 6.32 4.19 5.96 4.44L4.18 3.76C4 3.69 3.8 3.77 3.71 3.95L2.21 6.55C2.12 6.73 2.16 6.93 2.3 7.05L3.83 8.21C3.8 8.47 3.78 8.74 3.78 9C3.78 9.26 3.8 9.53 3.83 9.79L2.3 10.95C2.16 11.07 2.12 11.27 2.21 11.45L3.71 14.05C3.8 14.23 4 14.31 4.18 14.24L5.96 13.56C6.32 13.82 6.71 14.04 7.13 14.21L7.38 16.14C7.41 16.34 7.58 16.5 7.78 16.5H10.78C10.98 16.5 11.15 16.34 11.18 16.14L11.43 14.21C11.85 14.04 12.24 13.81 12.6 13.56L14.38 14.24C14.56 14.31 14.76 14.23 14.85 14.05L16.35 11.45C16.44 11.27 16.4 11.07 16.26 10.95L14.73 9.79Z"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <span>Settings</span>
                </button>

                <!-- Logout -->
                <button class="dropdown-item" (click)="logout()">
                  <div class="dropdown-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M6.75 15.75H3.375C2.96079 15.75 2.5625 15.5854 2.26884 15.2918C1.97518 14.9982 1.8125 14.5908 1.8125 14.1875V3.8125C1.8125 3.40921 1.97518 3.00179 2.26884 2.70813C2.5625 2.41447 2.96079 2.25 3.375 2.25H6.75"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M12.375 12.375L16.125 8.625L12.375 4.875"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M16.125 8.625H6.75"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </app-dropdown>
        </div>
      </div>

      <!-- Mobile Header -->
      <div class="header-content mobile-header">
        <!-- Left - Company Logo -->
        <app-company-switcher class="mobile-company-switcher" (dropdownOpenChange)="companySwitcherOpenChange.emit($event)"></app-company-switcher>

        <!-- Center - Page Title -->
        <span class="mobile-page-title">{{ pageTitle() }}</span>

        <!-- Right - Search Icon + Hamburger Menu -->
        <div class="mobile-actions">
          <!-- Search Icon Button -->
          <button
            class="mobile-action-btn"
            (click)="toggleMobileSearch()"
            aria-label="Search"
          >
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none">
              <path
                d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M17.5001 17.5L13.9167 13.9167"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <!-- Hamburger Menu Button -->
          <button
            class="mobile-action-btn hamburger-btn"
            (click)="toggleMobileMenu()"
            aria-label="Open menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 12H21M3 6H21M3 18H21"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Search Overlay -->
      @if (showMobileSearch()) {
        <div class="mobile-search-overlay" (click)="closeMobileSearch()">
          <div class="mobile-search-container" (click)="$event.stopPropagation()">
            <div class="mobile-search-header">
              <button class="mobile-back-btn" (click)="closeMobileSearch()">
                <img  src="assets/images/back-btn.png" alt="logo" class="mobile-logo">
              </button>
              <span class="mobile-header-title">Global Search</span>
            </div>
            <div class='mobile-search-container-wrapper'>

            <div class="mobile-search-input-container">
              <svg class="search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M17.5001 17.5L13.9167 13.9167" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <input
                type="text"
                [(ngModel)]="searchQuery"
                (input)="onSearchInput($event)"
                (keydown)="onMobileSearchKeydown($event)"
                placeholder="Search"
                class="mobile-search-input"
                autocomplete="off"
                autofocus
              />
              <button
                class="filter-icon-btn"
                (click)="toggleMobileAdvancedSearch()"
                [class.active]="showMobileAdvancedSearch()"
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M2.5 3.75C2.5 3.41848 2.6317 3.10054 2.86612 2.86612C3.10054 2.6317 3.41848 2.5 3.75 2.5H16.25C16.5815 2.5 16.8995 2.6317 17.1339 2.86612C17.3683 3.10054 17.5 3.41848 17.5 3.75V5.48833C17.4999 5.93032 17.3242 6.35418 17.0117 6.66667L12.5 11.1783V17.365C12.5 17.5213 12.4601 17.6749 12.384 17.8114C12.3079 17.9479 12.1982 18.0627 12.0653 18.1449C11.9324 18.227 11.7806 18.2739 11.6245 18.2809C11.4684 18.2879 11.3131 18.2549 11.1733 18.185L8.07583 16.6367C7.90281 16.5502 7.75729 16.4172 7.65559 16.2526C7.55388 16.0881 7.50001 15.8984 7.5 15.705V11.1783L2.98833 6.66667C2.67575 6.35418 2.50009 5.93032 2.5 5.48833V3.75Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
            <!-- Mobile Global Search Results or Advanced Search -->
            @if (showMobileAdvancedSearch()) {
              <div class="mobile-advanced-search-container">
                <app-advanced-search
                  (closeAdvancedSearch)="closeMobileAdvancedSearch(); closeMobileSearch()"
                ></app-advanced-search>
              </div>
            } <!-- @else if (searchQuery() && hasGlobalSearchPermission()) {
              <div class="mobile-search-results">
                <app-global-search
                  [text]="searchQuery()"
                  (closeGlobalSearch)="closeMobileSearch()"
                ></app-global-search>
              </div>
            } -->
            </div>

          </div>
        </div>
      }
    </header>
    <!-- User Profile Modal -->
    <app-resizable-panel
      [isVisible]="showProfile()"
      (visibilityChange)="showProfile.set($event)"
      [component]="UserProfileComponent"
      [componentInputs]="{ Id: currentUser().id }"
      [defaultWidthPercent]="38"
      [minWidthPercent]="30"
      [maxWidthPercent]="40"
      (closeClicked)="closeProfileModal()"
    >
    </app-resizable-panel>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .header-container {
        background-color: var(--colors-background-primary);
        padding: 16px 15px;
        z-index: 1000;
        position: relative;
        transition: var(--component-transition);
      }

      .header-content {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 40px;
        max-width: 100%;
      }

      /* Brand Section */
      .brand-section {
        width: 338px;
        flex-shrink: 0;
      }

      .brand-container {
        display: flex;
        align-items: center;
        gap: 40px;
        padding: 8px 16px;
        background-color: var(--colors-background-card);
        border: 1px solid var(--colors-border-primary);
        border-radius: 15px;
        width: fit-content;
        transition: var(--component-transition);
      }

      .brand-content {
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .logo-container {
        width: 30px;
        height: 30px;
        border-radius: 7px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .logo-icon {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .brand-text {
        color: var(--colors-text-primary);
        font-weight: 300;
        font-size: 16px;
        line-height: 38px;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        transition: var(--component-transition);
      }

      .dropdown-arrow {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--colors-text-muted);
        transition: var(--component-transition);
      }

      /* Search Section */
      .search-section {
        flex: 1;
        max-width: 802px;
      }

      .search-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 20px;
        background-color: var(--colors-background-secondary);
        border: 1px solid var(--colors-border-muted);
        border-radius: 15px;
        height: 56px;
        transition: var(--component-transition);
      }

      /* Remove bottom border radius when dropdown is open */
      .search-container.dropdown-open {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-bottom-color: transparent;
      }

      /* Highlight filter button when advanced search is active */
      .search-container:has(+ .global-search-dropdown app-advanced-search) .filter-btn {
        color: var(--colors-brand-primary);
      }

      .search-input-group {
        display: flex;
        align-items: center;
        gap: 5px;
        flex: 1;
      }

      .search-icon-container {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--colors-text-secondary);
        transition: var(--component-transition);
      }

      .search-input {
        background: transparent;
        border: none;
        outline: none;
        padding-left: 5px;
        color: var(--colors-text-primary);
        font-weight: 300;
        font-size: 16px;
        line-height: 38px;
        letter-spacing: 0.01em;
        flex: 1;
        min-width: 0;
        box-shadow: none;
        transition: var(--component-transition);
      }

      .search-input:focus {
        outline: none;
        border: none;
        box-shadow: none;
      }

      .search-input::placeholder {
        color: var(--colors-text-muted);
      }

      .command-badge {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 6px 8px;
        background-color: var(--colors-background-card);
        border: 1px solid var(--colors-border-primary);
        border-radius: 8px;
        min-width: 35px;
        height: 31px;
        white-space: nowrap;
        transition: var(--component-transition);
      }

      .command-badge span {
        color: var(--colors-text-muted);
        font-size: 12px;
        font-weight: 400;
        line-height: 38px;
        transition: var(--component-transition);
      }

      .filter-btn {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--colors-text-secondary);
        transition: var(--component-transition);
      }

      .filter-btn:hover {
        color: var(--colors-text-primary);
      }

      .filter-btn.active {
        color: var(--colors-brand-primary);
      }

      /* Actions Section */
      .actions-section {
        width: 338px;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 40px;
        flex-shrink: 0;
      }

      .action-icons {
        display: flex;
        align-items: center;
        gap: 20px;
      }

      .action-btn {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--colors-text-secondary);
        transition: var(--component-transition);
        border-radius: 8px;
        position: relative;
      }

      .action-btn:hover {
        color: var(--colors-text-primary);
        background-color: var(--colors-interactive-hover);
      }

      .action-btn.active {
        color: var(--colors-brand-primary);
        background-color: var(--colors-interactive-active);
      }

      .action-btn.has-notifications {
        color: var(--colors-brand-primary);
      }

      .theme-toggle-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        padding: 0;
        line-height: 0;

        svg {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }
      }

      .theme-toggle-btn:hover,
      .theme-toggle-btn:focus,
      .theme-toggle-btn:active {
        background: transparent;
        outline: none;
        box-shadow: none;
      }

      .notification-badge {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 8px;
        height: 8px;
        background-color: #ef4444;
        border-radius: 50%;
        border: 2px solid #ffffff;
      }

      .mobile-menu-btn {
        display: none;
      }

      /* Profile Section */
      .profile-trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        // background-color: #ffffff;
        // border: 1px solid #e0e0e0;
        border-radius: 15px;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
      }

      .profile-trigger:hover {
        background-color: #f9fafb;
      }

      .profile-info {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .user-avatar {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        text-align: center;
        line-height: 1;
        flex-shrink: 0;
      }

      .user-initials {
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        line-height: 1;
        letter-spacing: 0.5px;
        /* --avatar-text-color is set as an inline CSS variable on the parent div.
           !important ensures this wins over any global dark-mode span overrides. */
        color: var(--avatar-text-color) !important;
      }

      .user-name {
        // color: #121212;
        font-weight: 300;
        font-size: 16px;
        line-height: 38px;
        white-space: nowrap;
      }

      .menu-dots {
        width: 19px;
        height: 19px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6b7280;
      }

      /* Dropdown Styles */
      .profile-dropdown-content {
        padding: 8px 16px;
        min-width: 163px;
        width: 100%;
      }

      .dropdown-menu {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .dropdown-item {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 12px;
        border-radius: 8px;
        background: transparent;
        border: none;
        cursor: pointer;
        transition: all 0.2s ease-in-out;
        width: 100%;
        text-align: left;
      }

      .dropdown-item:hover {
        background-color: #f3f4f6;
      }

      .dropdown-icon {
        width: 18px;
        height: 18px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6b7280;
      }

      .dropdown-item span {
        color: #121212;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.03em;
      }

      /* Mobile Header Styles */
      .mobile-header {
        display: none;
      }

      .mobile-page-title {
        font-size: 16px;
        font-weight: 500;
        color: var(--colors-text-primary);
        white-space: nowrap;
        text-align: center;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        max-width: calc(100% - 140px);
        overflow: hidden;
        text-overflow: ellipsis;
        pointer-events: none;
      }

      .mobile-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .mobile-action-btn {
        width: 44px;
        height: 44px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--colors-text-primary);
        border-radius: 10px;
        transition: background-color 0.2s ease;
      }

      .mobile-action-btn:hover,
      .mobile-action-btn:active {
        background-color: var(--colors-interactive-hover);
      }

      /* Mobile Search Overlay */
      .mobile-search-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--colors-background-primary);
        animation: fadeIn 0.2s ease-out;
        height:100vh;
      }

      .mobile-search-container {
        display: flex;
        flex-direction: column;
        height: 100%;
      }

      .mobile-search-header {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px 20px;
        background-color: var(--colors-background-primary);
      }

      .mobile-back-btn {
       width: 40px;
        height: 40px;
        position: absolute;
        left: 20px;
        top: 50%;
        transform: translateY(-50%);
        display: flex;
        align-items: center;
        justify-content: center;
        background: #292929;
        border: none;
        border-radius: 50%;
        cursor: pointer;
        color: white;
      }

      .mobile-header-title {
        font-size: 16px;
        font-weight: 500;
        color: var(--colors-text-primary);
      }

      .mobile-search-input-container {
        display: flex;
        align-items: center;
        gap: 10px;
        margin: 0 20px 16px;
        padding: 12px 16px;
        background-color: var(--colors-background-tertiary);
        border: 1px solid var(--colors-border-muted);
        border-radius: 12px;
      }

      .mobile-search-input-container .search-icon {
        color: var(--colors-text-muted);
        flex-shrink: 0;
      }

      .mobile-search-input {
        flex: 1;
        height: 24px;
        padding: 0;
        font-size: 16px;
        border: none;
        background: transparent;
        color: var(--colors-text-primary);
        outline: none;
      }

      .mobile-search-input::placeholder {
        color: var(--colors-text-muted);
      }

      .filter-icon-btn {
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: transparent;
        border: none;
        cursor: pointer;
        color: var(--colors-text-muted);
        flex-shrink: 0;
        transition: color 0.2s ease;
      }

      .filter-icon-btn.active {
        color: var(--colors-brand-primary, #3b82f6);
      }

      .mobile-advanced-search-container {
        flex: 1;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        background: transparent;
        border-radius: 0;
        margin-top: 0;
      }

      .mobile-search-results {
        flex: 1;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      /* Mobile Responsive */
      @media (max-width: 768px) {
        .mobile-search-overlay {
          /* Must sit above mobile sidebar (z-index: 2001) to prevent overlap */
          z-index: 3000;
        }

        .header-container {
          padding: 8px 16px;
          /* iOS Safari safe area support */
          padding-top: max(8px, env(safe-area-inset-top, 8px));
        }

        .mobile-search-container-wrapper{
          height: 100%;
          background: #F0F4F5;
          padding: 15px;
          border-radius: 40px 40px 0 0;
          /* Safari fix for border-radius clipping */
          -webkit-transform: translateZ(0);
          transform: translateZ(0);
          -webkit-mask-image: -webkit-radial-gradient(white, black);
        }
        .mobile-search-input-container{
          background:white;
          border-radius:55px;
          padding: 15px;
          margin:0;
          font-weight: 300;
        }
        .desktop-header {
          display: none;
        }

        .mobile-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          position: relative;
          z-index: 1;
          min-height: 44px;
        }

        .mobile-company-switcher {
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }

        .mobile-actions {
          flex-shrink: 0;
          position: relative;
          z-index: 1;
        }
      }

      /* Tablet Responsive */
      @media (min-width: 769px) and (max-width: 1024px) {
        .header-content {
          gap: 24px;
        }

        .brand-section {
          width: 280px;
        }

        .actions-section {
          width: 280px;
          gap: 24px;
        }

        .action-icons {
          gap: 16px;
        }
      }

      /* Focus states for accessibility */
      .action-btn:focus,
      .filter-btn:focus,
      .profile-trigger:focus,
      .dropdown-item:focus {
        outline: 2px solid #3b82f6;
        outline-offset: 2px;
      }

      .action-btn:focus:not(:focus-visible),
      .filter-btn:focus:not(:focus-visible),
      .profile-trigger:focus:not(:focus-visible),
      .dropdown-item:focus:not(:focus-visible) {
        outline: none;
      }

      /* Smooth transitions */
      * {
        transition:
          background-color 0.2s ease-in-out,
          border-color 0.2s ease-in-out,
          color 0.2s ease-in-out;
      }

      /* Global Search Dropdown */
      .global-search-dropdown {
        position: absolute;
        top: 100%; /* No gap - starts right where search bar ends */
        left: 0;
        right: 0;
        background: var(--colors-background-card);
        border: 1px solid var(--colors-border-muted);
        border-top: none; /* Remove top border for seamless connection */
        border-bottom-left-radius: 15px;
        border-bottom-right-radius: 15px;
        border-top-left-radius: 0;
        border-top-right-radius: 0;
        box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        z-index: 9999;
        max-height: 80vh;
        overflow: hidden;
        animation: slideDown 0.2s ease-out;
      }

      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      /* Advanced search mode — allow dropdown menus to overflow the container */
      .global-search-dropdown.advanced-search-mode {
        overflow: visible;
        max-height: none;
      }

      .global-search-dropdown.closing {
        animation: slideUp 0.2s ease-in forwards;
      }

      @keyframes slideUp {
        from {
          opacity: 1;
          transform: translateY(0);
        }
        to {
          opacity: 0;
          transform: translateY(-10px);
        }
      }

      :host-context(.dark) {
        .profile-trigger:hover {
          background-color: var(--colors-background-elevated, #2e2f36) !important;
        }

        .dropdown-item:hover {
          background-color: var(--colors-background-elevated, #2e2f36) !important;
        }

        .dropdown-item span {
          color: var(--colors-text-primary, #e4e5f0) !important;
        }

        .search-container {
          .search-input {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }

          .search-icon-container svg path {
            stroke: var(--colors-text-muted) !important;
          }

          .command-badge {
            background-color: var(--colors-background-elevated) !important;
            border-color: var(--colors-border-muted) !important;
          }
        }

.mobile-search-container-wrapper {
        background-color:#22232A;
      }
          .mobile-search-input-container{
            background-color:black
          }
      }
    `,
  ],
})
export class HeaderComponent {
  @Output() sidebarToggle = new EventEmitter<void>();
  @Output() mobileSearchOpenChange = new EventEmitter<boolean>();

  // Reference to dropdown for programmatic control
  // (Assumes template ref: #profileDropdown on <app-dropdown>)
  // @ViewChild('profileDropdown') profileDropdown?: DropdownComponent;

  showProfile = signal(false);
  UserProfileComponent = UserProfileComponent;

  authService = inject(AuthService);
  themeService = inject(EnhancedThemeService);
  router = inject(Router);
  apiClient = inject(ApiClientService);
  searchService = inject(SearchService);
  customerProfileService = inject(CustomerProfileService);
  notificationService = inject(NotificationService);
  companyService = inject(CompanyService);
  sendDataService = inject(SendDataService);
  permissionService = inject(PermissionService);

  @Output() companySwitcherOpenChange = new EventEmitter<boolean>();

  // Component state using signals
  searchQuery = signal('');
  showFilter = signal(false);
  showAdvancedSearch = signal(false);
  hasNotifications = signal(true);
  isVisible = signal(true);
  isDarkMode = signal(false);
  showGlobalSearch = signal(false);
  isClosingGlobalSearch = signal(false);
  showMobileSearch = signal(false);
  showMobileAdvancedSearch = signal(false);
  isMobile = signal(false);

  // Global search container visibility (based on permission)
  showGlobalSearchContainer = signal(false);
  // Tab-level permissions to pass down to global-search component
  globalSearchTabPermissions = signal<Record<string, boolean>>({});

  // Use computed signal for reactive user data
  currentUser = computed(() => {
    const user = this.authService.user();
    return user ? { name: user.name, id: user.id || String(user.userID) } : { name: '', id: '' };
  });
  isMac = signal(false);

  // Page title based on current route
  pageTitle = signal('Dashboard');

  constructor() {
    // Detect operating system
    this.detectOperatingSystem();

    // Detect mobile on init
    this.checkMobile();

    // Initialize theme state
    toObservable(this.themeService.effectiveTheme).subscribe(theme => {
      this.isDarkMode.set(theme === 'dark');
    });

    // React to auth state changes
    effect(() => {
      const user = this.authService.user();
      if (user) {
        console.log('User data loaded in header:', user.name);
      }
    });

    // Add global keyboard shortcut listener
    this.setupGlobalKeyboardShortcuts();

    // Setup click outside listener to close global search
    this.setupClickOutsideListener();

    // Listen to route changes for page title and close search panels
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.updatePageTitle();
      if (this.showGlobalSearch()) {
        this.closeGlobalSearch();
      }
      if (this.showAdvancedSearch()) {
        this.closeAdvancedSearch();
      }
    });

    // Set initial page title
    this.updatePageTitle();

    // Open advanced search panel when triggered from search-result edit icon
    this.searchService.openAdvancedSearchPanel$.subscribe(open => {
      if (open) {
        this.showFilter.set(true);
        this.showAdvancedSearch.set(true);
        this.searchService.resetOpenAdvancedSearchPanel();
      }
    });

    // Load global search container and tab permissions
    this.loadGlobalSearchPermission();
  }

  /**
   * Fetch MENUS > HEADER_MENU > GOBAL_SEARCH children permissions.
   * Shows the search container only when at least one child tab has CanView === 1.
   * Passes the tab-level permission map to app-global-search.
   */
  private async loadGlobalSearchPermission(): Promise<void> {
    try {
      // Read directly from IndexedDB — no API call.
      // The sidebar populates IndexedDB via updatePermission() on app init.
      const allCategories: any[] = await this.permissionService.getAllResourceCategories();

      const menusCategory = allCategories.find((c: any) => c.Name === 'MENUS');
      const headerMenuType = menusCategory?.resource_types?.find((t: any) => t.Name === 'HEADER_MENU');
      const globalSearchResource = headerMenuType?.resources?.find((r: any) => r.Name === 'GOBAL_SEARCH' || r.Name === 'GLOBAL_SEARCH');

      const children: any[] = globalSearchResource?.children || [];

      // Map child Name → badge dsc key (Description can be null so Name is more reliable)
      const nameToTabMap: Record<string, string> = {
        'CUSTOMER_TAB':     'customers',
        'ORDER_TAB':        'orders',
        'SUBSCRIPTION_TAB': 'subscriptions',
        'TICKET_TAB':       'tickets',
        'PAYMENT_TAB':      'payments', 
      };

      // Container visibility:
      // If children exist → show container when at least one child has CanView === 1
      // If no children   → fall back to GLOBAL_SEARCH's own effective permission
      let showContainer = false;
      if (children.length > 0) {
        showContainer = children.some((child: any) => {
          const perms: any[] = child?.resource_permission || [];
          return perms[0]?.CanView === 1;
        });
      } else {
        const ownPerms: any[] = globalSearchResource?.resource_permission || [];
        const ownEffective = ownPerms[ownPerms.length - 1];
        showContainer = ownEffective?.CanView === 1;
      }
      this.showGlobalSearchContainer.set(showContainer);

      // Tab visibility: build per-tab map from children.
      // Empty map → global-search keeps all badges visible by default.
      const permissionsMap: Record<string, boolean> = {};
      children.forEach((child: any) => {
        const dsc = nameToTabMap[child.Name] ?? child.Description?.toLowerCase();
        // Use effective (backend-computed) permission — benchmark entry with no identity fields
        if (dsc) {
          const perms: any[] = child?.resource_permission || [];
          permissionsMap[dsc] = perms[0]?.CanView === 1;
        }
      });

      this.globalSearchTabPermissions.set(permissionsMap);
    } catch (error) {
      console.error('Error loading global search permission:', error);
      this.showGlobalSearchContainer.set(false);
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.checkMobile();
  }

  private checkMobile(): void {
    if (typeof window !== 'undefined') {
      this.isMobile.set(window.innerWidth < 768);
    }
  }

  private updatePageTitle(): void {
    const fullUrl = this.router.url;
    const url = fullUrl.split('?')[0]; // Remove query params

    // Tab-to-title mapping for customer dashboard (including child tabs)
    const tabTitleMap: { [key: string]: string } = {
      'customer-profile': 'Customer Profile',
      'billing-invoice': 'Billing & Invoice',
      'billing-invoice/adhoc-invoice': 'Ad-hoc Invoice',
      'billing-invoice/invoices': 'Invoice',
      'billing-invoice/payment-history': 'Payment History',
      'order-history': 'Order History',
      'purchase-history': 'Purchase History',
      'tickets': 'Tickets',
      'customer-history': 'Customer History',
      'activity-logs': 'Activity Logs',
      'saved-cards': 'Saved Cards',
      'files': 'Files'
    };

    // Check if we're on customer dashboard and have a tab parameter
    if (url === '/customers/dashboard' || url.startsWith('/customers/dashboard/')) {
      const urlParams = new URLSearchParams(fullUrl.split('?')[1] || '');
      const tab = urlParams.get('tab');
      if (tab) {
        // Check exact match first
        if (tabTitleMap[tab]) {
          this.pageTitle.set(tabTitleMap[tab]);
          return;
        }
        // Check if it's a child tab (contains /)
        if (tab.includes('/')) {
          const parentTab = tab.split('/')[0];
          if (tabTitleMap[parentTab]) {
            this.pageTitle.set(tabTitleMap[tab] || tabTitleMap[parentTab]);
            return;
          }
        }
      }
      // Default to Customer Profile if no tab or unknown tab
      this.pageTitle.set('Customer Profile');
      return;
    }

    const titleMap: { [key: string]: string } = {
      '/': 'Dashboard',
      '/dashboard': 'Dashboard',
      '/customers/dashboard': 'Customer Profile',
      '/customers': 'Customers',
      '/customers/list': 'Customers',
      '/customers/enrollment': 'Customer Enrollment',
      '/customers/purchase-history': 'Purchase History',
      '/orders': 'Orders',
      '/review': 'Order Review',
      '/porting': 'Porting',
      '/port-in': 'Port In',
      '/subscription': 'Subscriptions',
      '/subscriptions': 'Subscriptions',
      '/fullfillment': 'Fullfillment',
      '/awaiting-shipment': 'Awaiting Shipment',
      '/ticket-system/analytics': 'Ticket Analytics',
      '/ticket-system/create': 'Create Ticket',
      '/ticket-system': 'Tickets',
      '/tickets': 'Tickets',
      '/bulk-process': 'Bulk Process',
      '/bulk-operations': 'Bulk Process',
      '/bulk-operations/dashboard': 'Bulk Dashboard',
      '/bulk-operations/user-operations': 'User Operations',
      '/bulk-operations/user-operations/aps-change': 'APS Change',
      '/bulk-operations/user-operations/plan-change': 'Plan Change',
      '/payments': 'Payments',
      '/transaction': 'Payments',
      '/reports': 'Reports',
      '/new-reports': 'Reports',
      '/renewal-reports': 'Renewal Reports',
      '/executive-dashboard': 'Executive Dashboard',
      '/search-result': 'Search Results',
      '/search-dashboard': 'Search Dashboard',
      '/customer-email': 'Customer Email',
      '/tag-manager': 'Tags Manager',
      '/user-settings': 'Settings',
      '/settings': 'Settings',
      '/inventory': 'Inventory',
    };

    // Find matching route (check longer routes first for better matching)
    const sortedRoutes = Object.keys(titleMap).sort((a, b) => b.length - a.length);
    for (const route of sortedRoutes) {
      if (url === route || url.startsWith(route + '/') || url.startsWith(route + '?')) {
        this.pageTitle.set(titleMap[route]);
        return;
      }
    }

    // Default title
    this.pageTitle.set('Dashboard');
  }

  // Search functionality
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.searchQuery.set(value);

    // Push to search service so global-search component reacts via its searchTerm$ subscription
    this.searchService.updateSearchTerm(value);

    // Show global search if not already shown (and not mid-close animation)
    if (!this.showGlobalSearch() && !this.isClosingGlobalSearch()) {
      this.showGlobalSearch.set(true);
    }
  }

  onSearchInputClick(event: Event): void {
    event.stopPropagation();

    // If already shown OR currently in closing animation, do nothing.
    // Without the isClosingGlobalSearch() check, clicking during the 200ms
    // close animation would pass the guard and trigger a destroy+recreate cycle.
    if (this.showGlobalSearch() || this.isClosingGlobalSearch()) {
      console.log('🔵 Global search already open or closing - no action needed');
      return;
    }

    console.log('🖱️ Search input clicked - opening global search');

    // Clear search query and update service to ensure fresh state
    this.searchQuery.set('');
    this.searchService.updateSearchTerm('');

    // Show the component (will trigger ngOnInit)
    this.showGlobalSearch.set(true);
  }

  onSearchInputFocus(event: FocusEvent): void {
    event.stopPropagation();

    // If already shown, do nothing
    if (this.showGlobalSearch()) {
      console.log('🔵 Global search already open - focus event ignored');
      return;
    }

    console.log('🎯 Search input focused - opening global search');

    // Clear search query and update service to ensure fresh state
    this.searchQuery.set('');
    this.searchService.updateSearchTerm('');

    // Show the component (will trigger ngOnInit)
    this.showGlobalSearch.set(true);
  }

  closeGlobalSearch(): void {
    if (this.isClosingGlobalSearch()) return;
    this.isClosingGlobalSearch.set(true);
    setTimeout(() => {
      this.showGlobalSearch.set(false);
      this.isClosingGlobalSearch.set(false);
      this.searchQuery.set('');
      this.searchService.updateSearchTerm('');
    }, 200);
  }

  private setupClickOutsideListener(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('click', (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const searchSection = target.closest('.search-section');

        // If click is outside the search section, close both global and advanced search
        if (!searchSection) {
          if (this.showGlobalSearch()) {
            this.closeGlobalSearch();
          }
          if (this.showAdvancedSearch()) {
            this.closeAdvancedSearch();
          }
        }
      });
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.performSearch(this.searchQuery());
      // Close UI only — do NOT call closeGlobalSearch() here as it clears
      // searchService.updateSearchTerm('') which wipes state.query before
      // the async API call completes, breaking the search-result badge.
      this.showGlobalSearch.set(false);
      this.searchQuery.set('');
    }

    // Handle Escape key to close search dropdowns
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.showAdvancedSearch()) {
        this.closeAdvancedSearch();
      } else {
        this.closeGlobalSearch();
      }
    }

    // Handle Cmd+K on Mac or Ctrl+K on Windows/Linux
    if ((this.isMac() && event.metaKey && event.key === 'k') ||
      (!this.isMac() && event.ctrlKey && event.key === 'k')) {
      event.preventDefault();
      const input = event.target as HTMLInputElement;
      input.focus();
      input.select();
    }
  }

  async performSearch(query: string): Promise<void> {
    if (!query?.trim()) {
      return;
    }

    try {
      // Use the new search service
      const result = await this.searchService.performImmediateSearch(query.trim());

      if (result.success) {
        // Handle navigation based on search results
        switch (result.navigationAction) {
          case 'single':
            // Single customer found - navigate to customer profile
            this.router.navigate(['/customers/dashboard'], {
              queryParams: { customerId: result.result.data?.data[0]?.CustomerID },
            });
            break;

          case 'multiple':
            // Multiple customers found - navigate to search results page
            this.router.navigate(['/search-result']);
            break;

          case 'none':
            // No results found - stay on search-result to show empty state
            this.router.navigate(['/search-result']);
            break;
        }

        // Clear search input after successful search
        this.searchQuery.set('');
      } else {
        // Handle search errors
        console.error('Search failed:', result.error);
        this.notificationService.error(
          'Search Failed',
          result.error || 'Unable to search customers'
        );

        // Redirect to dashboard on error
        this.router.navigate(['/dashboard']);
        this.searchQuery.set('');
      }
    } catch (error: any) {
      console.error('Search error:', error);
      this.notificationService.error(
        'Search Error',
        'An unexpected error occurred while searching'
      );
      // Redirect to dashboard on unexpected errors
      this.router.navigate(['/dashboard']);
      this.searchQuery.set('');
    }
  }

  // Action handlers
  toggleFilter(): void {
    const newFilterState = !this.showFilter();
    this.showFilter.set(newFilterState);

    // Toggle between global search and advanced search
    if (newFilterState) {
      // Show advanced search, hide global search
      this.showAdvancedSearch.set(true);
      this.showGlobalSearch.set(false);
    } else {
      // Show global search if input is focused or has value
      this.showAdvancedSearch.set(false);
      if (this.searchQuery()) {
        this.showGlobalSearch.set(true);
      }
    }
  }

  closeAdvancedSearch(): void {
    this.showAdvancedSearch.set(false);
    this.showFilter.set(false);
  }

  toggleNotifications(): void {
    // Implement notifications toggle
    console.log('Toggle notifications');
  }

  toggleVisibility(): void {
    this.isVisible.set(!this.isVisible());
    // Implement visibility toggle logic
  }

  toggleMobileMenu(): void {
    this.sidebarToggle.emit();
  }

  toggleMobileSearch(): void {
    this.showMobileSearch.set(true);
    this.searchQuery.set('');
    this.mobileSearchOpenChange.emit(true);
  }

  closeMobileSearch(): void {
    this.showMobileSearch.set(false);
    this.searchQuery.set('');
    this.searchService.updateSearchTerm('');
    this.mobileSearchOpenChange.emit(false);
  }

  onMobileSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      // Only call customer search API when global search permission is NOT granted
      // if (!this.hasGlobalSearchPermission()) {
        this.performSearch(this.searchQuery());
      // }
      this.closeMobileSearch();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeMobileSearch();
    }
  }

  toggleMobileAdvancedSearch(): void {
    this.showMobileAdvancedSearch.set(!this.showMobileAdvancedSearch());
  }

  closeMobileAdvancedSearch(): void {
    this.showMobileAdvancedSearch.set(false);
  }

  // Navigation handlers
  // Open Profile Modal and send user id
  openProfileModal(): void {
    // Send user id to the service (structure matches what UserProfileComponent expects)
    this.sendDataService.setData({ __raw: { id: this.currentUser().id } });
    this.showProfile.set(true);

    // Optionally close dropdown if you add a template ref
    // if (this.profileDropdown) {
    //   this.profileDropdown.closeDropdown();
    // }
  }

  // Close Profile Modal
  closeProfileModal(): void {
    this.showProfile.set(false);
  }

  // (Retain for navigation if needed elsewhere)
  navigateToProfile(): void {
    this.router.navigate(['/profile']);
  }

  navigateToSettings(): void {
    this.router.navigate(['/settings']);
  }

  logout(): void {
    this.authService.logout();
  }

  // Company methods
  getCurrentCompany(): string {
    return localStorage.getItem('currentCompany') || 'unity';
  }


  // OS Detection and Keyboard Shortcut methods
  private detectOperatingSystem(): void {
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent || navigator.platform || '';
      this.isMac.set(/Mac|iPhone|iPod|iPad/i.test(userAgent));
    }
  }

  getKeyboardShortcut(): string {
    return this.isMac() ? '⌘K' : 'Ctrl+K';
  }

  private setupGlobalKeyboardShortcuts(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', (event: KeyboardEvent) => {
        // Check for Cmd+K on Mac or Ctrl+K on Windows/Linux
        if ((this.isMac() && event.metaKey && event.key === 'k') ||
          (!this.isMac() && event.ctrlKey && event.key === 'k')) {
          event.preventDefault();
          this.focusSearchInput();
        }
      });
    }
  }

  private focusSearchInput(): void {
    const searchInput = document.querySelector('.search-input') as HTMLInputElement;
    if (searchInput) {
      searchInput.focus();
      searchInput.select();
      // Explicitly open global search for keyboard shortcut (Ctrl+K / Cmd+K)
      if (!this.showGlobalSearch()) {
        this.searchQuery.set('');
        this.searchService.updateSearchTerm('');
        this.showGlobalSearch.set(true);
      }
    }
  }

  // Avatar methods
  getUserInitials(): string {
    const name = this.currentUser().name || '';
    const words = name.trim().split(' ').filter(word => word.length > 0);

    if (words.length === 0) {
      return 'U';
    } else if (words.length === 1) {
      return words[0].charAt(0).toUpperCase();
    } else {
      return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
    }
  }

  /** Returns a consistent bg/text hex pair for the current user's name.
   *  Each pair is designed for ≥4.5:1 contrast and reads well in both light and dark mode. */
  getUserColorPair(): { bg: string; text: string } {
    const pairs: { bg: string; text: string }[] = [
      { bg: '#BE9FFE', text: '#3F1596' }, // purple
      { bg: '#A8D8F0', text: '#1A4F72' }, // sky blue
      { bg: '#A9DFBF', text: '#1E6B45' }, // mint green
      { bg: '#F9C9A3', text: '#7A3B0E' }, // peach
      { bg: '#F5A6B8', text: '#7B1230' }, // rose
      { bg: '#B3D9FF', text: '#14325C' }, // cornflower
      { bg: '#FFD6A0', text: '#6B3800' }, // amber
      { bg: '#C4B6E8', text: '#38206A' }, // violet
      { bg: '#A8E6CF', text: '#1B5E3D' }, // seafoam
      { bg: '#FFB3B3', text: '#7B1414' }, // coral
      { bg: '#FFEAA7', text: '#6B5900' }, // yellow
      { bg: '#D5EAD0', text: '#2D5A27' }, // sage
      { bg: '#F0C9F0', text: '#5C1F5C' }, // lavender pink
      { bg: '#C9E4F5', text: '#1A3D55' }, // powder blue
      { bg: '#FFD1DC', text: '#7A1A30' }, // blush
    ];

    const name = this.currentUser().name || '';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return pairs[Math.abs(hash) % pairs.length];
  }

  // Theme-related methods
  // isDarkMode(): boolean {
  //   return this.themeService.effectiveTheme() === 'dark';
  // }

  // UI state methods
  // hasNotifications(): boolean {
  //   return false; // Placeholder - integrate with notification service later
  // }

  // isVisible(): boolean {
  //   return true; // Placeholder - integrate with visibility state later
  // }

  // Event handlers
  // toggleNotifications(): void {
  //   console.log('Toggle notifications clicked');
  //   // Integrate with notification service later
  // }

  // toggleVisibility(): void {
  //   console.log('Toggle visibility clicked');
  //   // Integrate with visibility service later
  // }

  // toggleMobileMenu(): void {
  //   console.log('Toggle mobile menu clicked');
  //   // Integrate with mobile menu service later
  // }

  // navigateToProfile(): void {
  //   this.router.navigate(['/profile']);
  // }

  // navigateToSettings(): void {
  //   this.router.navigate(['/settings']);
  // }

  // logout(): void {
  //   this.authService.logout();
  // }
}
